import React, { useEffect } from 'react';
import EventDisplay from './EventDisplay';

/**
 * A dedicated page for displaying the event presentation in full screen.
 * This page is designed to be shown on a large display at the event.
 * 
 * @param {Object} props - Component props
 * @param {string} props.eventType - The type of event to display (default, conference, festival, etc.)
 */
interface EventDisplayPageProps {
    eventType?: string;
}

const EventDisplayPage: React.FC<EventDisplayPageProps> = ({ eventType = 'default' }) => {
    // Prevent screen from sleeping with proper lifecycle management
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null;

        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLock = await navigator.wakeLock.request('screen');
                }
            } catch (err) {
                console.error(`Failed to get Wake Lock: ${err}`);
            }
        };

        // Re-acquire wake lock when page becomes visible again
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };

        requestWakeLock();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'f') {
                document.documentElement.requestFullscreen?.()
                    .catch(err => console.error(`Fullscreen error: ${err}`));
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('keydown', handleKeyDown);
            wakeLock?.release();
        };
    }, []);

    return (
        <div className="w-full h-screen overflow-hidden bg-black">
            <EventDisplay eventType={eventType} />

            {/* Fullscreen button */}
            <button
                onClick={() => {
                    if (document.documentElement.requestFullscreen) {
                        document.documentElement.requestFullscreen();
                    }
                }}
                className="fixed top-4 right-4 bg-black/50 backdrop-blur-sm text-white p-2 rounded-full z-50 hover:bg-black/70 transition-colors"
                title="Enter fullscreen (or press 'f')"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                </svg>
            </button>
        </div>
    );
};

export default EventDisplayPage; 
